import React from "react";
import { LANDING } from "@/constants/testIds";

const FEATURED = {
  slug: "raver",
  code: "DROP 001",
  title: "Raver Club",
  lifestyle: "Underground · Late Nights · Warehouse",
  status: "Live Now",
  statusTone: "live",
  img: "https://customer-assets.emergentagent.com/job_members-first/artifacts/wugexth3_B997ED53-118A-42F3-84F4-AF75F6A7C8FF.png",
  accentDot: "#ff2a2a",
  quote: "For the ones who leave last.",
};

const CLUBS = [
  {
    slug: "airport",
    code: "DROP 002",
    title: "Airport Club",
    lifestyle: "Travel · Holidays · Departures",
    img: "https://customer-assets.emergentagent.com/job_members-first/artifacts/ydnn2jif_2D244B9F-9CE8-4105-8F0D-50C0D8AE5D93.png",
    accentDot: "#3ea6a6",
  },
  {
    slug: "casino",
    code: "DROP 003",
    title: "Casino Club",
    lifestyle: "Gambling · Luxury · High Rollers",
    img: "https://customer-assets.emergentagent.com/job_members-first/artifacts/c8j3b4l5_6537A28E-6384-463A-8B07-F0DB02C0294B.png",
    accentDot: "#c4334a",
  },
  {
    slug: "mushroom",
    code: "DROP 004",
    title: "Mushroom Club",
    lifestyle: "Trippy · Free · Other Worlds",
    img: "https://customer-assets.emergentagent.com/job_members-first/artifacts/06dpy7x2_E8596FE9-6F05-4BF4-A055-EFA95AFDED51.png",
    accentDot: "#a85cf0",
  },
  {
    slug: "festival",
    code: "DROP 005",
    title: "Festival Club",
    lifestyle: "Outdoor Music · Freedom · Sun",
    img: "https://customer-assets.emergentagent.com/job_members-first/artifacts/5kubrbdt_560B036C-A98E-42BF-9552-80AFFDDD0E54.png",
    accentDot: "#e26ea5",
  },
  {
    slug: "beach",
    code: "DROP 006",
    title: "Beach Club",
    lifestyle: "Summer · Cocktails · Coastal",
    img: "https://customer-assets.emergentagent.com/job_members-first/artifacts/qq6luqcp_5396F091-A6BC-4637-8BBA-5792183862A5.png",
    accentDot: "#ff6b9d",
  },
  {
    slug: "afterhours",
    code: "DROP 007",
    title: "After Hours Club",
    lifestyle: "Late Nights · Stories · Unforgettable",
    img: "https://customer-assets.emergentagent.com/job_members-first/artifacts/mphi2c4j_6DA06D64-7337-4F49-97C0-DDACEDF2AFEF.png",
    accentDot: "#ff7a18",
  },
];

const ClubCard = ({ c, testId }) => {
  const light = c.onWhiteBg;
  return (
    <article
      data-testid={testId}
      className={`archive-card relative aspect-[3/4] group cursor-default overflow-hidden ${
        light ? "bg-[hsl(var(--scrm-cream))]" : "bg-[hsl(var(--scrm-bg))]"
      }`}
    >
      {c.cover ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-70 group-hover:opacity-90 transition-opacity duration-700"
            style={{ backgroundImage: `url(${c.img})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--scrm-bg))]/40 via-transparent to-[hsl(var(--scrm-bg))]/95" />
        </>
      ) : (
        <img
          src={c.img}
          alt={`${c.title} mascot character`}
          loading="lazy"
          className="absolute inset-x-0 bottom-0 top-12 md:top-16 w-full h-[78%] object-contain transition-transform duration-[1400ms] group-hover:scale-[1.04]"
        />
      )}

      {/* Top meta */}
      <div className="absolute top-4 left-4 right-4 flex items-start justify-between z-10">
        <span
          className={`text-[10px] tracking-eyebrow uppercase ${
            light ? "text-[hsl(var(--scrm-bg))]/70" : "text-[hsl(var(--scrm-cream))]/70"
          }`}
        >
          {c.code}
        </span>
        <span className="flex items-center gap-1.5 text-[10px] tracking-eyebrow uppercase">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: c.accentDot }}
          />
          <span
            className={
              light
                ? "text-[hsl(var(--scrm-bg))]/70"
                : "text-[hsl(var(--scrm-cream))]/80"
            }
          >
            Coming Soon
          </span>
        </span>
      </div>

      {/* Bottom title block */}
      <div
        className={`absolute bottom-0 inset-x-0 p-5 md:p-6 z-10 ${
          light
            ? "bg-gradient-to-t from-[hsl(var(--scrm-cream))] via-[hsl(var(--scrm-cream))]/90 to-transparent"
            : "bg-gradient-to-t from-[hsl(var(--scrm-bg))] via-[hsl(var(--scrm-bg))]/85 to-transparent"
        }`}
      >
        <div
          className={`font-display uppercase tracking-tight text-2xl md:text-3xl leading-tight ${
            light ? "text-[hsl(var(--scrm-bg))]" : "text-[hsl(var(--scrm-cream))]"
          }`}
        >
          {c.title}
        </div>
        <div
          className={`mt-2 font-body text-[11px] tracking-[0.18em] uppercase ${
            light
              ? "text-[hsl(var(--scrm-bg))]/60"
              : "text-[hsl(var(--scrm-cream))]/55"
          }`}
        >
          {c.lifestyle}
        </div>
      </div>
    </article>
  );
};

export default function Archive() {
  return (
    <section
      data-testid={LANDING.archiveSection}
      className="relative bg-[hsl(var(--scrm-bg))] border-t hairline"
    >
      {/* Header */}
      <div className="px-6 md:px-10 pt-20 md:pt-28 pb-12 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mb-4">
              ✦ The Universe
            </div>
            <h2 className="font-display uppercase tracking-mega text-[hsl(var(--scrm-cream))] text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.92]">
              A collection of<br />experiences.
            </h2>
          </div>
          <p className="lg:col-span-5 font-body text-sm md:text-base text-[hsl(var(--scrm-cream))]/65 leading-relaxed max-w-md">
            SCRM is built around themed clubs and collectible characters. Each club is
            a world — travel, gambling, festivals, nightlife, summer, late nights.
            Every release is a souvenir from a place worth being.
          </p>
        </div>
      </div>

      {/* Featured drop (live) */}
      <div className="px-6 md:px-10 mb-3 md:mb-4">
        <article
          data-testid={LANDING.archiveCard(FEATURED.slug)}
          className="relative overflow-hidden border hairline border-[hsl(var(--scrm-cream))]/10 bg-[hsl(var(--scrm-bg))] group"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 min-h-[460px] md:min-h-[520px]">
            {/* Left: copy */}
            <div className="md:col-span-7 lg:col-span-8 p-8 md:p-12 lg:p-16 flex flex-col justify-between gap-10 order-2 md:order-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/70">
                  {FEATURED.code} · Live Now
                </span>
                <span className="flex items-center gap-2 text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/85">
                  <span
                    className="h-1.5 w-1.5 rounded-full animate-pulse"
                    style={{ background: FEATURED.accentDot }}
                  />
                  {FEATURED.status}
                </span>
              </div>

              <div>
                <h3 className="font-display uppercase tracking-mega text-[hsl(var(--scrm-cream))] text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
                  Raver<br />Club.
                </h3>
                <p className="mt-5 md:mt-6 font-body text-sm md:text-base text-[hsl(var(--scrm-cream))]/65 max-w-md leading-relaxed">
                  {FEATURED.quote} Collecting stories, sunrises and memories that
                  don't need explaining.
                </p>
                <div className="mt-6 font-body text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--scrm-cream))]/55">
                  {FEATURED.lifestyle}
                </div>
              </div>
            </div>

            {/* Right: character */}
            <div className="md:col-span-5 lg:col-span-4 relative min-h-[320px] md:min-h-0 order-1 md:order-2 border-t md:border-t-0 md:border-l hairline border-[hsl(var(--scrm-cream))]/10 overflow-hidden">
              <img
                src={FEATURED.img}
                alt="Raver Club mascot — DJ panda"
                className="absolute inset-0 w-full h-full object-contain p-6 md:p-8 transition-transform duration-[1400ms] group-hover:scale-[1.04]"
              />
            </div>
          </div>
        </article>
      </div>

      {/* Universe grid */}
      <div className="px-6 md:px-10 pb-24 md:pb-28">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {CLUBS.map((c) => (
            <ClubCard key={c.slug} c={c} testId={LANDING.archiveCard(c.slug)} />
          ))}
        </div>

        <div className="relative mt-14 md:mt-20 max-w-3xl mx-auto">
          <img
            src="https://customer-assets.emergentagent.com/job_members-first/artifacts/cwvnjqlx_27861062-0C71-4A6A-9AB4-A7D149BFB6F9.png"
            alt="SCRM mascot in flight"
            className="hidden md:block absolute -left-10 -top-10 w-32 lg:w-40 opacity-90 select-none pointer-events-none"
          />
          <p className="text-center font-display uppercase tracking-[0.04em] text-[hsl(var(--scrm-cream))]/85 text-lg md:text-2xl leading-tight">
            SCRM is not just clothing. It is a collection of experiences.
          </p>
          <img
            src="https://customer-assets.emergentagent.com/job_members-first/artifacts/cwvnjqlx_27861062-0C71-4A6A-9AB4-A7D149BFB6F9.png"
            alt=""
            aria-hidden="true"
            className="md:hidden block mx-auto mt-8 w-28 opacity-90 select-none pointer-events-none"
          />
        </div>
      </div>
    </section>
  );
}
